import React, { useState } from 'react';
import emailjs from '@emailjs/browser';
import { useInView } from '../../hooks';
import { Button, Input, Textarea } from '../ui';
import { LocationIcon, EmailIcon, PhoneIcon } from '../common/Icons';
import { partnersData, companyInfo } from '../../data/siteData';
import { EMAILJS, isEmailJSReady } from '../../config/emailjs';

// ─── helpers ─────────────────────────────────────────────────────────────────

const sendWhatsApp = async (payload) => {
  try {
    await fetch('/api/whatsapp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    // best-effort — do not block form submission
  }
};

// ─── Partners Section ─────────────────────────────────────────────────────────

export const PartnersSection = () => (
  <section className="py-16 border-y border-white/10">
    <div className="max-w-7xl mx-auto px-6">
      <p className="text-center text-gray-500 text-sm tracking-widest mb-8">
        TRUSTED BY INDUSTRY LEADERS
      </p>
      <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
        {partnersData.map((partner) => (
          <div
            key={partner.id}
            className="text-2xl font-gaming text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
          >
            {partner.name}
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ─── Contact Section (homepage quick form) ────────────────────────────────────

export const ContactSection = () => {
  const [ref, isInView] = useInView(0.1);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setErrorMsg('');

    const templateParams = {
      from_name:        form.name,
      from_email:       form.email,
      contact_subject:  form.subject,
      contact_message:  form.message,
      sent_at:          new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' }),
    };

    try {
      if (!isEmailJSReady()) throw new Error('Email service not configured');
      await emailjs.send(EMAILJS.serviceId, EMAILJS.contactTemplate, templateParams, EMAILJS.publicKey);
      await sendWhatsApp({ type: 'contact', ...form });
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setErrorMsg('Could not send your message. Please email us directly at paulnkingwa34@gmail.com');
    }
  };

  return (
    <section id="contact" className="py-24">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="section-subtitle text-primary-500">GET IN TOUCH</p>
          <h2 className="section-title">
            LET'S <span className="text-gradient">CONNECT</span>
          </h2>
          <p className="section-description">
            Ready to collaborate or have questions? We'd love to hear from you.
          </p>
        </div>

        <div
          ref={ref}
          className={`bg-white/5 rounded-3xl border border-white/10 p-8 md:p-12 transition-all duration-1000 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          {status === 'success' ? (
            <div className="text-center py-10">
              <div className="text-6xl mb-5">🎮</div>
              <h3 className="font-gaming text-2xl text-white mb-3">MESSAGE SENT!</h3>
              <p className="text-gray-400 mb-3">Thanks! We'll get back to you within 24 hours.</p>
              <p className="text-gray-500 text-sm mb-6">You'll also receive a WhatsApp notification shortly.</p>
              <div className="flex flex-wrap gap-3 justify-center">
                <button
                  onClick={() => setStatus('idle')}
                  className="btn-ghost text-sm"
                >
                  Send another message
                </button>
                <a
                  href={`https://wa.me/255627857470?text=${encodeURIComponent('Hi Paul, I just sent you a message via the Riven Byte website.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-green-500/15 border border-green-500/30 text-green-400 text-sm hover:bg-green-500/25 transition-colors"
                >
                  Also say hi on WhatsApp →
                </a>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="grid md:grid-cols-2 gap-6">
                <Input type="text" name="name" placeholder="Your Name" value={form.name} onChange={handleChange} required />
                <Input type="email" name="email" placeholder="Email Address" value={form.email} onChange={handleChange} required />
              </div>
              <div className="mt-6">
                <Input type="text" name="subject" placeholder="Subject" value={form.subject} onChange={handleChange} required />
              </div>
              <div className="mt-6">
                <Textarea name="message" placeholder="Your Message" rows={5} value={form.message} onChange={handleChange} required />
              </div>
              {status === 'error' && (
                <p className="mt-4 text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3">
                  {errorMsg}
                </p>
              )}
              <Button type="submit" variant="primary" className="w-full mt-6" disabled={status === 'sending'}>
                {status === 'sending' ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending…
                  </span>
                ) : (
                  'SEND MESSAGE'
                )}
              </Button>
            </form>
          )}
        </div>

        {/* Contact Info */}
        <div className="grid md:grid-cols-3 gap-8 mt-12 text-center">
          <div>
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary-500/20 flex items-center justify-center text-primary-500">
              <LocationIcon />
            </div>
            <h4 className="font-bold mb-1">Location</h4>
            <p className="text-gray-500 text-sm">{companyInfo.location}</p>
          </div>
          <div>
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-secondary-400/20 flex items-center justify-center text-secondary-400">
              <EmailIcon />
            </div>
            <h4 className="font-bold mb-1">Email</h4>
            <a href={`mailto:${companyInfo.email}`} className="text-gray-500 text-sm hover:text-white transition-colors">
              {companyInfo.email}
            </a>
          </div>
          <div>
            <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-purple-500/20 flex items-center justify-center text-purple-400">
              <PhoneIcon />
            </div>
            <h4 className="font-bold mb-1">Phone</h4>
            <a href={`tel:${companyInfo.phone}`} className="text-gray-500 text-sm hover:text-white transition-colors">
              {companyInfo.phone}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// ─── Newsletter Section (homepage subscribe) ──────────────────────────────────

export const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');

    const templateParams = {
      subscriber_email: email,
      sent_at: new Date().toLocaleString('en-US', { timeZone: 'Africa/Nairobi' }),
    };

    try {
      if (!isEmailJSReady()) throw new Error('Email service not configured');
      const tmpl = EMAILJS.newsletterTemplate || EMAILJS.contactTemplate;
      await emailjs.send(EMAILJS.serviceId, tmpl, templateParams, EMAILJS.publicKey);
      await sendWhatsApp({ type: 'newsletter', email });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-16 bg-gradient-to-r from-primary-500/10 via-secondary-400/10 to-primary-500/10">
      <div className="max-w-4xl mx-auto px-6 text-center">
        <h3 className="font-gaming text-2xl md:text-3xl font-bold mb-4">
          JOIN THE <span className="text-primary-500">REVOLUTION</span>
        </h3>
        <p className="text-gray-400 mb-8">
          Subscribe for exclusive updates, early access, and behind-the-scenes content
        </p>

        {status === 'success' ? (
          <div className="max-w-lg mx-auto">
            <p className="text-secondary-400 font-semibold text-lg mb-2">You're in! 🎮</p>
            <p className="text-gray-400 text-sm">
              Thanks for subscribing — we'll keep you posted on every drop.
            </p>
            <button
              onClick={() => setStatus('idle')}
              className="mt-4 text-xs text-gray-600 hover:text-gray-400 underline"
            >
              Subscribe another email
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
            <Input
              type="email"
              placeholder="Enter your email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="flex-1"
              disabled={status === 'sending'}
            />
            <Button type="submit" variant="primary" className="whitespace-nowrap" disabled={status === 'sending'}>
              {status === 'sending' ? (
                <span className="flex items-center gap-2">
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  …
                </span>
              ) : (
                'SUBSCRIBE'
              )}
            </Button>
          </form>
        )}

        {status === 'error' && (
          <p className="mt-3 text-red-400 text-sm">
            Something went wrong.{' '}
            <a href="mailto:paulnkingwa34@gmail.com" className="underline">Email us directly</a>
          </p>
        )}
      </div>
    </section>
  );
};

